import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, AlertTriangle } from "lucide-react";
import UserRow from "@/pages/admin/UserRow";
import UserDetailDialog from "@/pages/admin/UserDetailDialog";
import AddUserDialog from "@/pages/admin/AddUserDialog";
import EditUserDialog from "@/pages/admin/EditUserDialog";
import Pager from "@/pages/admin/Pager";

const emptyNew = { name: "", phone: "", role: "customer", referral_code_used: "", affiliate_type: "", associate_firm: "" };

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [setupFilter, setSetupFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [chain, setChain] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState(emptyNew);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(() => {
    const params = { q, limit, skip };
    if (role !== "all") params.role = role;
    if (setupFilter !== "all") params.setup = setupFilter;
    api.get("/admin/users", { params }).then((r) => {
      setUsers(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [q, role, setupFilter, limit, skip]);
  useEffect(() => { load(); }, [load]);
  // Reset to page 0 whenever a filter changes.
  useEffect(() => { setSkip(0); }, [q, role, setupFilter]);

  const showDetail = async (uid) => {
    const d = await api.get(`/admin/users/${uid}`);
    const c = await api.get(`/admin/users/${uid}/referral-chain`);
    setDetail(d.data);
    setChain(c.data);
  };
  const closeDetail = () => { setDetail(null); setChain(null); };

  const addUser = async () => {
    try {
      if (newUser.role === "affiliate" && !newUser.affiliate_type) {
        return toast.error("Please select an affiliate type");
      }
      await api.post("/admin/users/manual", newUser);
      toast.success("User added");
      setAddOpen(false);
      setNewUser(emptyNew);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const startDelete = async (u) => {
    // Fetch pending referral count for warning
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setDeleting({ user: u, pendingRefs: data.pending_referrals ?? 0 });
    } catch {
      setDeleting({ user: u, pendingRefs: 0 });
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleting.user.id}`);
      toast.success(`Deleted ${deleting.user.name} · ${data.pending_referrals_removed} pending referral(s) removed`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally { setDeleteBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="velo-card p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <Input data-testid="input-search-users" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, or code" className="velo-input pl-10 h-11" />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="select-role-filter" className="velo-input h-11 w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#22103F] border-white/10 text-white">
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="affiliate">Affiliate</SelectItem>
            </SelectContent>
          </Select>
          <Select value={setupFilter} onValueChange={setSetupFilter}>
            <SelectTrigger data-testid="select-setup-filter" className="velo-input h-11 w-44"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#22103F] border-white/10 text-white">
              <SelectItem value="all">All setup states</SelectItem>
              <SelectItem value="complete">Setup complete</SelectItem>
              <SelectItem value="pending">Pending setup</SelectItem>
            </SelectContent>
          </Select>
          <AddUserDialog open={addOpen} onOpenChange={setAddOpen} newUser={newUser} setNewUser={setNewUser} onSubmit={addUser} />
        </div>
      </div>

      <div className="velo-card overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-[#170929] border-b border-white/10">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Phone</th>
              <th className="px-6 py-4">Role</th>
              <th className="px-6 py-4">Login code</th>
              <th className="px-6 py-4">Setup</th>
              <th className="px-6 py-4 text-right">Balance</th>
              <th className="px-6 py-4">Joined</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                onView={() => showDetail(u.id)}
                onEdit={() => setEditing(u)}
                onDelete={() => startDelete(u)}
              />
            ))}
            {users.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-500 text-sm">No users found</td></tr>
            ) : null}
          </tbody>
        </table>
        <Pager
          total={total}
          limit={limit}
          skip={skip}
          testidPrefix="pager-users"
          onChange={({ limit: l, skip: s }) => { setLimit(l); setSkip(s); }}
        />
      </div>

      <UserDetailDialog detail={detail} chain={chain} onClose={closeDetail} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} onSaved={load} />

      <Dialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Delete {deleting?.user?.name}?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-400">This will permanently remove the user account.</p>
            {deleting?.pendingRefs > 0 ? (
              <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 flex gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" strokeWidth={2} />
                <div className="text-sm text-amber-100">
                  This user has <span className="font-mono font-medium">{deleting.pendingRefs}</span> pending referral{deleting.pendingRefs === 1 ? "" : "s"}. Those pending entries will also be deleted. Closed referrals and past redemptions remain in the ledger.
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5">Cancel</Button>
            <Button data-testid="btn-confirm-delete-user" onClick={confirmDelete} disabled={deleteBusy} className="rounded-full bg-red-500 hover:bg-red-400 text-white">
              {deleteBusy ? "Deleting…" : "Delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
